import { CalendarCheck2, HelpCircle, Minus } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { YStack } from "tamagui";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import type { SheetProps } from "../../../ui/Sheet";
import { Title } from "../../../ui/Title";
import { DatePicker } from "../../components/FormDataModal";
import { formatDateRange } from "../../dayJs";
import { groupDateRanges } from "../../groupDates";
import { CreateWizardButton } from "./CreateWizardButton";
import { CreateWizardProgress } from "./CreateWizardProgress";
import { type CreateWizardState, CreateWizardStep } from "./CreateWizardState";
import { Selection } from "./Selection";

const WizardStepDatePicker: React.FC<
  { state: CreateWizardState } & Omit<SheetProps, "sheetId">
> = observer(({ state, ...props }) => {
  return (
    <DatePicker
      calendarProps={{ minDate: new Date().toISOString().split("T")[0] }}
      header={
        <YStack gap="$small">
          <Title>Select date</Title>
          <Body>Select one or multiple dates</Body>
        </YStack>
      }
      value={state.calendarModule?.dates}
      onSelect={(date) => {
        const currentDates = state.calendarModule?.dates ?? [];

        let newDates: string[] = [...currentDates];

        if (currentDates.some((d) => d === date)) {
          newDates = newDates.filter((d) => d !== date);
        } else {
          newDates.push(date);
        }

        state.setCalendarModule({ dates: newDates, skip: false });
      }}
      footer={
        <CreateWizardButton
          disabled={!state.calendarModule?.dates}
          onPress={() => state.setActiveStepId(CreateWizardStep.LOCATION)}
          subText={
            state.calendarModule?.dates
              ? groupDateRanges(state.calendarModule?.dates.slice())
                  .map((range) => formatDateRange(range, true))
                  .join(", ")
              : ""
          }
        >
          Continue
        </CreateWizardButton>
      }
      {...props}
      sheetId="date"
    />
  );
});

export const WizardStepDate: React.FC<{ state: CreateWizardState }> = observer(
  (props) => {
    const [datePickerOpen, setDatePickerOpen] = useState(false);

    return (
      <YStack gap="$medium">
        <Box backgroundColor={"$primary"} gap={"$small"}>
          <Title>Do you know the date?</Title>
          <Body>
            Do you know the date for when this is happening, or does it even
            matter?
          </Body>

          <CreateWizardProgress state={props.state} />
        </Box>

        <Selection
          onSelect={() => {
            setDatePickerOpen(true);
          }}
          title={"Yes, we know the date!"}
          description="If you already know the date we'd love to know what it is to help you further"
          icon={CalendarCheck2}
        />

        <Selection
          onSelect={() => {
            props.state.setCalendarModule({ skip: false });
            props.state.setActiveStepId(CreateWizardStep.LOCATION);
          }}
          title={"No, we don't know the date"}
          description="Planira will help you and your group find the perfect date"
          icon={HelpCircle}
        />

        <Selection
          onSelect={() => {
            props.state.setCalendarModule({ skip: true });
            props.state.setActiveStepId(CreateWizardStep.LOCATION);
          }}
          title={"Skip for now"}
          description="Planira can help you later on to find the perfect date"
          icon={Minus}
        />
        <WizardStepDatePicker
          state={props.state}
          onOpenChange={(open: boolean) => setDatePickerOpen(open)}
          open={datePickerOpen}
        />
      </YStack>
    );
  },
);
